import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import CTABanner from '@/components/feature/CTABanner';
import FAQAccordion from '@/components/base/FAQAccordion';
import { cabines, Cabine } from '@/mocks/cabines';
import CuradoriaGaleria from '@/components/feature/CuradoriaGaleria';
import { divisoriasAcustica } from '@/mocks/curadoria';
import CabineCard from './components/CabineCard';
import CabineModal from './components/CabineModal';
import { useSEO } from '@/hooks/useSEO';

const categories = ['Todos', 'Individual', 'Dupla', 'Reunião', 'Estúdio', 'Lounge'];

const benefits = [
  { icon: 'ri-volume-mute-line', title: 'Isolamento Acústico', desc: 'Redução de até 45dB de ruído externo para total concentração e privacidade.' },
  { icon: 'ri-tools-line', title: 'Sem Obra', desc: 'Instalação modular rápida, sem necessidade de reforma ou obra no espaço.' },
  { icon: 'ri-map-pin-line', title: 'Relocável', desc: 'Pode ser desmontada e remontada em outro local sempre que necessário.' },
  { icon: 'ri-leaf-line', title: 'Sustentável', desc: 'Materiais ecológicos e sistema de ventilação de baixo consumo energético.' },
  { icon: 'ri-wifi-line', title: 'Conectividade', desc: 'Tomadas, USB e opção de Wi-Fi integrados para total produtividade.' },
  { icon: 'ri-palette-line', title: 'Personalizável', desc: 'Cores, acabamentos e configurações sob medida para sua identidade visual.' },
];

const faqs = [
  { q: 'O que é uma cabine acústica para escritório?', a: 'Uma cabine acústica é um espaço modular fechado instalado dentro do escritório que oferece privacidade e isolamento de ruído de até 45dB, ideal para ligações, reuniões e trabalho concentrado em ambientes de open office.' },
  { q: 'Precisa de obra para instalar uma cabine acústica?', a: 'Não. As cabines acústicas da Home Office Premium são 100% modulares e não requerem nenhuma obra, quebra de parede ou reforma. A instalação é rápida e o escritório continua funcionando normalmente durante o processo.' },
  { q: 'Qual o isolamento acústico das cabines?', a: 'Nossas cabines oferecem redução de até 45dB de ruído externo, garantindo privacidade total para conversas e concentração para trabalho individual.' },
  { q: 'As cabines acústicas podem ser removidas ou relocadas?', a: 'Sim. Por serem modulares, as cabines podem ser desmontadas e remontadas em outro local sempre que necessário, sem deixar nenhum dano na estrutura do imóvel.' },
  { q: 'A Home Office Premium entrega cabines acústicas em Recife e Pernambuco?', a: 'Sim. Atendemos Caruaru e toda a região de Pernambuco e Nordeste, incluindo Recife, Petrolina, Garanhuns, Arcoverde e demais cidades, com entrega, instalação e suporte técnico inclusos.' },
];

export default function CabinesPage() {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedCabine, setSelectedCabine] = useState<Cabine | null>(null);

  useSEO({
    title: 'Cabines Acústicas para Escritório Caruaru PE | Home Office Premium',
    description: 'Cabines acústicas para escritório em Caruaru PE. Isolamento de até 45dB, instalação modular sem obra. Modelos individual, dupla, reunião e estúdio. Solicite orçamento.',
    keywords: 'cabines acústicas Recife, phone booth escritório Petrolina, isolamento acústico Garanhuns, cabines telefone Arcoverde, cabines reunião Sertão PE, cabines acústicas Pernambuco, cabines escritório Agreste, cabines individuais Nordeste, cabines duplas Recife, cabines estúdio Petrolina, cabines lounge Caruaru, cabines acústicas preço Recife',
    path: '/cabines-acusticas',
    ogTitle: 'Cabines Acústicas para Escritório | Home Office Premium Caruaru',
    ogDescription: 'Cabines acústicas com isolamento de até 45dB em Caruaru PE. Instalação modular sem obra.',
  });

  useEffect(() => {
    const schemaId = 'schema-cabines';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Cabines Acústicas para Escritório – Home Office Premium',
      description: 'Cabines acústicas modulares para escritório em Caruaru PE. Isolamento de até 45dB, sem obra, relocáveis e personalizáveis.',
      url: `${siteUrl}/cabines-acusticas`,
    });

    const itemListId = 'schema-cabines-itemlist';
    let itemListScript = document.getElementById(itemListId) as HTMLScriptElement | null;
    if (!itemListScript) { itemListScript = document.createElement('script'); itemListScript.id = itemListId; itemListScript.type = 'application/ld+json'; document.head.appendChild(itemListScript); }
    itemListScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Cabines Acústicas para Escritório – Home Office Premium Caruaru',
      url: `${siteUrl}/cabines-acusticas`,
      numberOfItems: cabines.length,
      itemListElement: cabines.map((cabine, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: cabine.name,
          description: cabine.description,
          image: cabine.image,
          brand: { '@type': 'Brand', name: cabine.brand },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'BRL',
            price: '0',
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: 'Home Office Premium' },
            url: `${siteUrl}/contato`,
            priceSpecification: {
              '@type': 'PriceSpecification',
              priceCurrency: 'BRL',
              description: 'Consulte o preço pelo WhatsApp ou formulário de contato',
            },
          },
        },
      })),
    });

    const breadcrumbId = 'schema-cabines-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Produtos', item: `${siteUrl}/produtos` },
        { '@type': 'ListItem', position: 3, name: 'Cabines Acústicas', item: `${siteUrl}/cabines-acusticas` },
      ],
    });

    const faqId = 'schema-cabines-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });

    return () => { script?.remove(); itemListScript?.remove(); bcScript?.remove(); faqScript?.remove(); };
  }, []);

  const filtered = activeCategory === 'Todos' ? cabines : cabines.filter((c) => c.category === activeCategory);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        eyebrow="Tendência 2025"
        eyebrowIcon="ri-fire-line"
        title={
          <>
            Cabines acústicas para <span className="text-white/50 font-light">focar no que importa.</span>
          </>
        }
        subtitle="Privacidade e foco no open office. Soluções modulares sem obra para reuniões, ligações, podcasts e trabalho concentrado."
        image="/media/ia/0204.webp"
        alt="Cabines acústicas para escritório corporativo em Caruaru PE"
        breadcrumb={[
          { label: 'Home', to: '/' },
          { label: 'Produtos', to: '/produtos' },
          { label: 'Cabines Acústicas' },
        ]}
        ctas={[
          { label: 'Solicitar orçamento', to: '/contato' },
          { label: 'Falar no WhatsApp', href: 'https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es', variant: 'secondary' },
        ]}
      />

      {/* Stats */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: 'até 45dB', label: 'Isolamento acústico' },
              { value: 'Sem obra', label: 'Instalação modular' },
              { value: '6 modelos', label: 'Disponíveis' },
              { value: '100%', label: 'Personalizável' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-foreground-900 font-semibold text-[24px] md:text-[32px] tracking-tight">{s.value}</p>
                <p className="text-foreground-400 text-[13px] font-light mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vantagens */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Por que escolher" title="Vantagens das cabines acústicas." className="mb-12" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b) => (
              <div key={b.title} className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                  <i className={`${b.icon} text-lg`}></i>
                </span>
                <div>
                  <h3 className="text-foreground-900 font-medium text-[15px] mb-1">{b.title}</h3>
                  <p className="text-foreground-500 text-[14px] leading-relaxed font-light">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modelos */}
      <section id="modelos" className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <SectionHeading eyebrow="Catálogo" title="Nossos modelos." />
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-[13px] font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat ? 'bg-foreground-950 text-white' : 'text-foreground-500 hover:text-foreground-900'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
            {filtered.map((cabine) => <CabineCard key={cabine.id} cabine={cabine} onSelect={setSelectedCabine} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Perguntas frequentes" title="FAQ — Cabines acústicas." className="mb-12" />
          <div className="max-w-3xl">
            {faqs.map((f) => (
              <FAQAccordion key={f.q} question={f.q} answer={f.a} />
            ))}
          </div>
        </div>
      </section>

      <CuradoriaGaleria
        eyebrow="Curadoria de fabricante · Gabbinetto"
        titulo="Cabines e tratamento acústico."
        descricao="Cabine Booth e soluções acústicas retiradas dos catálogos Gabbinetto Booth e Soluções Acústicas. Sem divulgar índice de atenuação sem ficha técnica do fabricante."
        produtos={divisoriasAcustica.slice(5)}
        contexto="cabines-acusticas"
        tom="soft"
      />

      <CTABanner
        title="Transforme seu escritório com cabines acústicas."
        subtitle="Fale com nossos especialistas e receba um projeto personalizado para o seu espaço, sem compromisso."
        primaryLabel="Solicitar orçamento"
        primaryTo="/contato"
      />

      <Footer />
      {selectedCabine && <CabineModal cabine={selectedCabine} onClose={() => setSelectedCabine(null)} />}
      <WhatsAppFloat />
    </div>
  );
}